import pLimit from 'p-limit';

/**
 * Utilities for managing concurrent async operations with configurable throttling
 * to prevent resource exhaustion while maintaining optimal throughput.
 */

// Maximum concurrent requests for optimal throughput
const OPTIMAL_CONCURRENCY = 10;

/**
 * Calculates optimal concurrency based on layer count.
 * Uses 10 concurrent requests for maximum throughput, but won't exceed
 * the actual number of layers to avoid unnecessary workers.
 *
 * @param layerCount - Number of layers to process
 * @returns Optimal concurrency level (capped at 10)
 *
 * @example
 * // With 8 layers, returns 8 (no need for 10 workers)
 * const concurrency = getOptimalConcurrency(8); // 8
 *
 * @example
 * // With 50 layers, returns 10 (capped for optimal throughput)
 * const concurrency = getOptimalConcurrency(50); // 10
 */
export function getOptimalConcurrency(layerCount: number): number {
  return Math.min(OPTIMAL_CONCURRENCY, layerCount);
}

/**
 * Executes promises with a concurrency limit to prevent resource exhaustion.
 *
 * @param tasks - Array of async task functions to execute
 * @param maxConcurrency - Maximum number of tasks to run concurrently
 * @returns Promise that resolves with array of results in original task order
 *
 * @example
 * const tasks = urls.map(url => () => fetch(url));
 * const results = await promiseAllWithConcurrency(tasks, 10);
 */
export async function promiseAllWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  maxConcurrency: number
): Promise<T[]> {
  if (tasks.length === 0) {
    return [];
  }

  const limit = pLimit(Math.max(1, maxConcurrency));
  return Promise.all(tasks.map((task) => limit(task)));
}
